/**
 * Custom JavaScript for Power Tool Services.
 *
 * ---------------------------------------------------------------------------
 * NATIVE INFINITE SCROLL FOR COLLECTION PAGES
 * ---------------------------------------------------------------------------
 * Replaces the Scrollify app using Shopify's Section Rendering API.
 *
 * Why this is SEO-safer than a typical infinite-scroll app:
 *   - The theme's real paginated <a href="?page=2"> links stay in the DOM.
 *     Google cannot click a "load more" button or trigger a scroll event, so
 *     those anchors are the only crawl path to products past page 1.
 *   - Each ?page=N URL still renders standalone and keeps rel=prev/next.
 *   - The numbered pagination is only hidden VISUALLY, and only once JS has
 *     run. With JS disabled, or for a crawler, normal pagination is intact.
 *   - history.replaceState keeps the address bar in step, so a refresh or a
 *     shared link lands somewhere sensible.
 *
 * Behaviour: auto-loads the next page when the sentinel scrolls into view,
 * up to AUTO_LOAD_LIMIT times, then switches to a click-to-load button. That
 * stops a 262-product collection from firing eleven sequential fetches, and
 * keeps the footer reachable.
 * ---------------------------------------------------------------------------
 */

(function () {
  'use strict';

  var AUTO_LOAD_LIMIT = 2;      // auto-loads before requiring a click
  var ROOT_MARGIN = '600px';    // start fetching before the sentinel is visible

  var state = {
    loading: false,
    autoLoads: 0,
    observer: null,
    sentinel: null,
    button: null
  };

  function injectStyles() {
    if (document.getElementById('pts-infinite-scroll-styles')) return;
    var style = document.createElement('style');
    style.id = 'pts-infinite-scroll-styles';
    style.textContent = [
      '.pts-is-active .pagination__nav,',
      '.pts-is-active .pagination__prev,',
      '.pts-is-active .pagination__next,',
      '.pts-is-active .pagination__page-count {',
      '  position: absolute; width: 1px; height: 1px; overflow: hidden;',
      '  clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap;',
      '}',
      '.pts-is-sentinel { width: 100%; height: 1px; }',
      '.pts-is-status { text-align: center; padding: 24px 0 8px; }',
      '.pts-is-status[hidden] { display: none; }',
      '.pts-is-button { min-width: 220px; }',
      '.pts-is-spinner {',
      '  display: inline-block; width: 22px; height: 22px; border-radius: 50%;',
      '  border: 2px solid currentColor; border-top-color: transparent;',
      '  animation: pts-is-spin .7s linear infinite; vertical-align: middle;',
      '}',
      '@keyframes pts-is-spin { to { transform: rotate(360deg); } }',
      '@media (prefers-reduced-motion: reduce) {',
      '  .pts-is-spinner { animation: none; border-top-color: currentColor; opacity: .5; }',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  }

  function qs(root, sel) { return root ? root.querySelector(sel) : null; }

  function getSection() {
    return document.querySelector('section[data-section-type="collection"]');
  }

  function getGrid(section) {
    return qs(section, '.product-list--collection');
  }

  function getNextLink(section) {
    return qs(section, '.pagination .pagination__next');
  }

  function sectionIdFor(section) {
    return section.getAttribute('data-section-id');
  }

  function buildSectionUrl(href, sectionId) {
    var url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function cleanUrl(href) {
    var url = new URL(href, window.location.origin);
    url.searchParams.delete('section_id');
    return url.pathname + url.search;
  }

  /**
   * Hide the per-page "Showing 1 - 24 of 262" counters once infinite scroll
   * engages. Rewriting them would mean rebuilding a translated string, which
   * breaks on any non-English locale, so hiding is the honest option. The
   * total-products counter is left alone.
   */
  function hideShowingCounters(section) {
    var nodes = section.querySelectorAll(
      '.collection__showing-count, .collection__products-count-showing'
    );
    Array.prototype.forEach.call(nodes, function (node) {
      node.setAttribute('hidden', 'hidden');
      node.style.display = 'none';
    });
  }

  function buildStatusBar(section) {
    var pagination = qs(section, '.pagination');
    if (!pagination) return null;

    var bar = document.createElement('div');
    bar.className = 'pts-is-status';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button--secondary pts-is-button';
    button.textContent = 'Load more products';
    button.setAttribute('hidden', 'hidden');

    var spinner = document.createElement('span');
    spinner.className = 'pts-is-spinner';
    spinner.setAttribute('hidden', 'hidden');
    spinner.setAttribute('role', 'status');
    spinner.setAttribute('aria-label', 'Loading more products');

    bar.appendChild(spinner);
    bar.appendChild(button);
    pagination.parentNode.insertBefore(bar, pagination);

    state.button = button;
    state.spinner = spinner;
    return bar;
  }

  function setLoading(isLoading) {
    state.loading = isLoading;
    if (state.spinner) {
      if (isLoading) state.spinner.removeAttribute('hidden');
      else state.spinner.setAttribute('hidden', 'hidden');
    }
    if (state.button && isLoading) state.button.setAttribute('hidden', 'hidden');
  }

  function showLoadButton() {
    if (state.button) state.button.removeAttribute('hidden');
  }

  function teardown() {
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }
    if (state.sentinel && state.sentinel.parentNode) {
      state.sentinel.parentNode.removeChild(state.sentinel);
    }
    state.sentinel = null;
    state.button = null;
    state.spinner = null;
    state.loading = false;
    state.autoLoads = 0;
  }

  function loadNextPage(section) {
    if (state.loading) return;

    var nextLink = getNextLink(section);
    if (!nextLink) { teardown(); return; }

    var href = nextLink.getAttribute('href');
    var sectionId = sectionIdFor(section);
    if (!href || !sectionId) return;

    setLoading(true);

    fetch(buildSectionUrl(href, sectionId), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('.product-list--collection');
        var currentGrid = getGrid(section);

        if (!newGrid || !currentGrid) { teardown(); return; }

        var added = [];
        while (newGrid.firstElementChild) {
          var item = newGrid.firstElementChild;
          newGrid.removeChild(item);
          currentGrid.appendChild(item);
          added.push(item);
        }

        // Swap in the new pagination block so the next link points one further
        // on, and the real anchors stay in the DOM for crawlers.
        var oldPagination = qs(section, '.pagination');
        var newPagination = doc.querySelector('.pagination');
        if (oldPagination) {
          if (newPagination) oldPagination.parentNode.replaceChild(newPagination, oldPagination);
          else oldPagination.parentNode.removeChild(oldPagination);
        }

        history.replaceState({}, '', cleanUrl(href));
        setLoading(false);

        /**
         * Let app scripts re-bind to the products we just injected. Wishlist
         * King, quick view and quick buy all attach on page load, so freshly
         * appended cards can come through inert without this. Verify on the
         * preview theme before going live.
         */
        document.documentElement.dispatchEvent(new CustomEvent('collection:products-appended', {
          bubbles: true,
          detail: { products: added, section: section }
        }));

        if (getNextLink(section)) {
          if (state.autoLoads >= AUTO_LOAD_LIMIT) showLoadButton();
          repositionSentinel(section);
        } else {
          teardown();
        }
      })
      .catch(function (error) {
        // Fall back to normal pagination rather than stranding the customer.
        console.error('[PTS] Infinite scroll failed, reverting to pagination:', error);
        setLoading(false);
        section.classList.remove('pts-is-active');
        teardown();
      });
  }

  function repositionSentinel(section) {
    var pagination = qs(section, '.pagination');
    if (state.sentinel && pagination && pagination.parentNode) {
      pagination.parentNode.insertBefore(state.sentinel, pagination);
    }
  }

  function init() {
    var section = getSection();
    if (!section) return;

    teardown();

    var grid = getGrid(section);
    var nextLink = getNextLink(section);
    if (!grid || !nextLink) return;

    if (!('IntersectionObserver' in window) || typeof DOMParser === 'undefined') {
      return; // Leave normal pagination in place on older browsers.
    }

    injectStyles();
    section.classList.add('pts-is-active');
    hideShowingCounters(section);
    buildStatusBar(section);

    if (state.button) {
      state.button.addEventListener('click', function () {
        loadNextPage(section);
      });
    }

    state.sentinel = document.createElement('div');
    state.sentinel.className = 'pts-is-sentinel';
    state.sentinel.setAttribute('aria-hidden', 'true');
    repositionSentinel(section);

    state.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        if (state.autoLoads >= AUTO_LOAD_LIMIT) { showLoadButton(); return; }
        state.autoLoads += 1;
        loadNextPage(section);
      });
    }, { rootMargin: ROOT_MARGIN });

    state.observer.observe(state.sentinel);
  }

  /**
   * Warehouse replaces .collection__dynamic-part wholesale when filters or
   * sorting change, which destroys our sentinel and pagination references.
   * Watch for that and re-initialise.
   */
  function watchForFilterUpdates() {
    var container = document.querySelector('.collection');
    if (!container || typeof MutationObserver === 'undefined') return;

    var debounce;
    new MutationObserver(function (mutations) {
      var replaced = mutations.some(function (m) {
        return Array.prototype.some.call(m.addedNodes, function (node) {
          return node.nodeType === 1 && (
            node.classList.contains('collection__dynamic-part') ||
            node.querySelector && node.querySelector('.product-list--collection')
          );
        });
      });
      if (!replaced) return;
      clearTimeout(debounce);
      debounce = setTimeout(init, 60);
    }).observe(container, { childList: true, subtree: true });
  }

  function boot() {
    init();
    watchForFilterUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.getAttribute('data-section-type') === 'collection') {
      setTimeout(boot, 50);
    }
  });
})();
